import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AttendanceService, AttendanceRecord, MonthlyStats, AbsenceRecord } from '../services/attendance.service';
import { LoginService } from '../login.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-attendance-report',
    templateUrl: './attendance-report.component.html',
    styleUrls: ['./attendance-report.component.scss'],
    standalone: false
})
export class AttendanceReportComponent implements OnInit {
  monthlyRecords: AttendanceRecord[] = [];
  monthlyStats: MonthlyStats | null = null;
  selectedYear: number;
  selectedMonth: number;
  selectedUserId: number | null = null;
  currentEmployeeName: string = '';
  currentEmployeeNumber: number | null = null;
  isLoading = false;
  error: string | null = null;
  currentYear = new Date().getFullYear();
  
  // Cached paired records to prevent constant re-rendering
  cachedGroupedRecordsWithPairs: { [key: string]: Array<{checkIn: AttendanceRecord | null, checkOut: AttendanceRecord | null}> } = {};
  
  // Fix missing time modal
  showFixModal = false;
  fixingRecord: AttendanceRecord | null = null;
  fixingDateKey: string = '';
  newTime: string = '';
  isFixing = false;
  fixError: string | null = null;

  // Absence records
  absenceRecords: AbsenceRecord[] = [];
  showAbsenceModal = false;
  isAbsenceSaving = false;
  absenceError: string | null = null;
  editingAbsenceId: number | null = null;
  absenceForm = {
    startDate: '',
    endDate: '',
    absenceType: 'vacation' as 'vacation' | 'sick',
    reason: ''
  };
  
  // Delete confirmation modal
  showDeleteConfirmModal = false;
  deletingAbsenceId: number | null = null;

  months = [
    { value: 1, label: 'appointments.gui.month.January' },
    { value: 2, label: 'appointments.gui.month.February' },
    { value: 3, label: 'appointments.gui.month.March' },
    { value: 4, label: 'appointments.gui.month.April' },
    { value: 5, label: 'appointments.gui.month.May' },
    { value: 6, label: 'appointments.gui.month.June' },
    { value: 7, label: 'appointments.gui.month.July' },
    { value: 8, label: 'appointments.gui.month.August' },
    { value: 9, label: 'appointments.gui.month.September' },
    { value: 10, label: 'appointments.gui.month.October' },
    { value: 11, label: 'appointments.gui.month.November' },
    { value: 12, label: 'appointments.gui.month.December' }
  ];

  constructor(
    private attendanceService: AttendanceService,
    private router: Router,
    public ls: LoginService,
    private translate: TranslateService
  ) {
    const now = new Date();
    this.selectedYear = now.getFullYear();
    this.selectedMonth = now.getMonth() + 1;
  }

  ngOnInit(): void {
    // Set initial selected user to current user
    if (this.ls.UID) {
      this.selectedUserId = this.ls.UID;
    }
    
    // Load users list if user has attendanceReports permission
    if (this.ls.premissions?.attendanceReports && this.ls.ClinicID) {
      this.ls.getClinicUsers(this.ls.ClinicID).subscribe({
        next: (users) => {
          this.ls.usersList = users;
          this.ls.filteredUserSearch = users;
          this.updateCurrentEmployeeInfo();
        },
        error: (err) => {
          console.error('Failed to load clinic users:', err);
        }
      });
    }
    
    this.updateCurrentEmployeeInfo();
    this.loadMonthlyReport();
    this.loadAbsenceRecords();
  }

  /**
   * Go back to appointments
   */
  goBack(): void {
    this.router.navigate(['/appointments']);
  }

  /**
   * Update current employee info based on selected user
   */
  updateCurrentEmployeeInfo(): void {
    if (this.selectedUserId && this.ls.usersList && this.selectedUserId !== this.ls.UID) {
      const selectedUser = this.ls.usersList.find(u => u.UID == this.selectedUserId);
      this.currentEmployeeName = selectedUser?.Name || '';
      this.currentEmployeeNumber = selectedUser?.ClinicEmployeeNum || null;
    } else {
      this.currentEmployeeName = this.ls.username || '';
      this.currentEmployeeNumber = this.ls.ClinicEmployeeNum || null;
    }
  }

  /**
   * Load monthly report
   */
  loadMonthlyReport(): void {
    this.isLoading = true;
    this.error = null;

    // Use selected user ID or current user ID
    const userId = this.selectedUserId || this.ls.UID;

    this.attendanceService.getMonthlyReport(this.selectedYear, this.selectedMonth, userId).subscribe(
      (response: any) => {
        if (response.success) {
          this.monthlyRecords = response.data || [];
          this.monthlyStats = response.stats || null;
          // Cache the paired records to prevent re-rendering
          this.updateCachedPairs();
        }
        this.isLoading = false;
        this.loadAbsenceRecords();
      },
      (err) => {
        this.isLoading = false;
        this.error = 'שגיאה בטעינת הדוח';
        console.error('Error:', err);
      }
    );
  }

  /**
   * Handle user change
   */
  onUserChange(): void {
    this.updateCurrentEmployeeInfo();
    this.loadMonthlyReport();
  }

  /**
   * Handle month change
   */
  onMonthChange(): void {
    this.loadMonthlyReport();
  }

  /**
   * Handle year change
   */
  onYearChange(): void {
    this.loadMonthlyReport();
  }

  /**
   * Get previous month
   */
  previousMonth(): void {
    if (this.selectedMonth === 1) {
      this.selectedMonth = 12;
      this.selectedYear--;
    } else {
      this.selectedMonth--;
    }
    this.loadMonthlyReport();
  }

  /**
   * Get next month
   */
  nextMonth(): void {
    if (this.selectedMonth === 12) {
      this.selectedMonth = 1;
      this.selectedYear++;
    } else {
      this.selectedMonth++;
    }
    this.loadMonthlyReport();
  }

  /**
   * Get month name
   */
  getMonthName(monthNum: number): string {
    const month = this.months.find(m => m.value === monthNum);
    return month?.label || '';
  }

  /**
   * Get translated month name for export
   */
  getTranslatedMonthName(monthNum: number): string {
    const monthKey = this.getMonthName(monthNum);
    return this.translate.instant(monthKey);
  }

  /**
   * Format time for display
   */
  formatTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleTimeString('he-IL', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  /**
   * Group records by date
   */
  getGroupedRecords(): { [key: string]: AttendanceRecord[] } {
    const grouped: { [key: string]: AttendanceRecord[] } = {};

    this.monthlyRecords.forEach(record => {
      const dateKey = this.formatDate(record.CreationTime);
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(record);
    });

    return grouped;
  }

  /**
   * Update cached paired records
   */
  updateCachedPairs(): void {
    const grouped = this.getGroupedRecords();
    this.cachedGroupedRecordsWithPairs = {};
    
    for (const dateKey in grouped) {
      this.cachedGroupedRecordsWithPairs[dateKey] = this.getPairedRecords(grouped[dateKey]);
    }
  }

  /**
   * Get sorted date keys
   */
  getSortedDateKeys(): string[] {
    return Object.keys(this.getGroupedRecords()).sort((a, b) => {
      return new Date(b).getTime() - new Date(a).getTime();
    });
  }

  /**
   * Pair check-ins with check-outs for a day
   */
  getPairedRecords(records: AttendanceRecord[]): Array<{checkIn: AttendanceRecord | null, checkOut: AttendanceRecord | null}> {
    const pairs: Array<{checkIn: AttendanceRecord | null, checkOut: AttendanceRecord | null}> = [];
    
    // Sort records by time (ascending - earliest first)
    const sorted = [...records].sort((a, b) => 
      new Date(a.CreationTime).getTime() - new Date(b.CreationTime).getTime()
    );
    
    let lastCheckIn: AttendanceRecord | null = null;
    
    for (const record of sorted) {
      if (record.CheckType === 'check-in') {
        if (lastCheckIn) {
          // Previous check-in had no check-out, add it as unpaired
          pairs.push({ checkIn: lastCheckIn, checkOut: null });
        }
        lastCheckIn = record;
      } else if (record.CheckType === 'check-out') {
        if (lastCheckIn) {
          // Found a pair
          pairs.push({ checkIn: lastCheckIn, checkOut: record });
          lastCheckIn = null;
        } else {
          // Check-out without check-in
          pairs.push({ checkIn: null, checkOut: record });
        }
      }
    }
    
    // If there's a remaining check-in without check-out
    if (lastCheckIn) {
      pairs.push({ checkIn: lastCheckIn, checkOut: null });
    }
    
    return pairs;
  }

  /**
   * Calculate hours for a pair
   */
  calculatePairHours(pair: {checkIn: AttendanceRecord | null, checkOut: AttendanceRecord | null}): string {
    if (!pair.checkIn || !pair.checkOut) {
      return '-';
    }

    const duration = (new Date(pair.checkOut.CreationTime).getTime() - new Date(pair.checkIn.CreationTime).getTime()) / (1000 * 60 * 60);
    return this.decimalHoursToText(duration);
  }

  private calculateDayHoursDecimal(records: AttendanceRecord[]): number {
    let totalHours = 0;
    const sorted = [...records].sort((a, b) =>
      new Date(a.CreationTime).getTime() - new Date(b.CreationTime).getTime()
    );
    let lastCheckIn: AttendanceRecord | null = null;
    for (const record of sorted) {
      if (record.CheckType === 'check-in') {
        lastCheckIn = record;
      } else if (record.CheckType === 'check-out' && lastCheckIn) {
        const duration = (new Date(record.CreationTime).getTime() - new Date(lastCheckIn.CreationTime).getTime()) / (1000 * 60 * 60);
        totalHours += duration;
        lastCheckIn = null;
      }
    }
    return totalHours;
  }

  /**
   * Calculate shift hours for a day
   */
  calculateDayHours(records: AttendanceRecord[]): string {
    return this.decimalHoursToText(this.calculateDayHoursDecimal(records));
  }

  /**
   * Check if a record has a matching pair
   */
  hasMatchingPair(record: AttendanceRecord, allRecords: AttendanceRecord[]): boolean {
    const sorted = [...allRecords].sort((a, b) => 
      new Date(a.CreationTime).getTime() - new Date(b.CreationTime).getTime()
    );
    
    const recordIndex = sorted.findIndex(r => 
      r.CreationTime === record.CreationTime && r.CheckType === record.CheckType
    );
    
    if (recordIndex === -1) return false;
    
    if (record.CheckType === 'check-in') {
      // Look for next check-out
      for (let i = recordIndex + 1; i < sorted.length; i++) {
        if (sorted[i].CheckType === 'check-out') {
          return true;
        } else if (sorted[i].CheckType === 'check-in') {
          return false; // Another check-in before check-out
        }
      }
      return false; // No check-out found
    } else {
      // Look for previous check-in
      for (let i = recordIndex - 1; i >= 0; i--) {
        if (sorted[i].CheckType === 'check-in') {
          return true;
        } else if (sorted[i].CheckType === 'check-out') {
          return false; // Another check-out before check-in
        }
      }
      return false; // No check-in found
    }
  }

  /**
   * Get daily exams count for a specific date
   */
  getDailyExamsCount(dateKey: string): number {
    if (!this.monthlyStats || !this.monthlyStats.dailyExams) {
      return 0;
    }
    
    // Convert dateKey format (DD.MM.YYYY) to ISO format (YYYY-MM-DD)
    const parts = dateKey.split('.');
    if (parts.length !== 3) {
      return 0;
    }
    
    const isoDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
    return this.monthlyStats.dailyExams[isoDate] || 0;
  }

  /**
   * Check if exams should be displayed based on user category
   */
  shouldShowExams(): boolean {
    if (!this.monthlyStats || !this.monthlyStats.userCategory) {
      return true; // Show by default if no category info
    }
    
    const category = this.monthlyStats.userCategory.toLowerCase();
    
    // Don't show exams for secretary or nurse
    const isDoctor = category.includes('doctor') 
    const isTech = category.includes('tech')
    
    return isDoctor || isTech;
  }

  private decimalHoursToText(decimal: number): string {
    const totalMinutes = Math.round(decimal * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    if (h === 0) return `${m} דק`;
    if (m === 0) return `${h} שע`;
    return `${h} שע ${m} דק`;
  }

  private getHebrewDayName(dateStr: string): string {
    const days = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
    return days[new Date(dateStr).getDay()];
  }

  exportToPDF(): void {
    const userName = this.currentEmployeeName || this.ls.username || '';
    const monthName = this.getTranslatedMonthName(this.selectedMonth);
    const title = `דוח נוכחות - ${userName} - ${monthName} ${this.selectedYear}`;

    const groupedRecords = this.getGroupedRecords();
    const dateKeys = Object.keys(groupedRecords).sort((a, b) => {
      const getTs = (key: string) => {
        const records = groupedRecords[key];
        return records.length ? new Date(records[0].CreationTime).getTime() : 0;
      };
      return getTs(a) - getTs(b);
    });

    let totalWorkHours = 0;
    const showExams = this.shouldShowExams();

    let daysHtml = '';
    dateKeys.forEach(dateKey => {
      const dayRecords = groupedRecords[dateKey];
      const dayHours = this.calculateDayHoursDecimal(dayRecords);
      totalWorkHours += dayHours;

      const dayName = this.getHebrewDayName(dayRecords[0].CreationTime);
      const pairs = this.cachedGroupedRecordsWithPairs[dateKey] || [];
      let rowsHtml = '';
      pairs.forEach(pair => {
        const checkIn = pair.checkIn ? this.formatTime(pair.checkIn.CreationTime) : '<span class="missing">⚠ חסר</span>';
        const checkOut = pair.checkOut ? this.formatTime(pair.checkOut.CreationTime) : '<span class="missing">⚠ חסר</span>';
        const pairDecimal = (pair.checkIn && pair.checkOut)
          ? (new Date(pair.checkOut.CreationTime).getTime() - new Date(pair.checkIn.CreationTime).getTime()) / (1000 * 60 * 60)
          : null;
        const hoursText = pairDecimal !== null ? this.decimalHoursToText(pairDecimal) : '-';
        rowsHtml += `<tr><td>${checkIn}</td><td>${checkOut}</td><td>${hoursText}</td></tr>`;
      });

      daysHtml += `
        <div class="day-block">
          <div class="day-header">
            <span class="day-date-name">${dateKey} &nbsp;יום ${dayName}</span>
            <span class="day-total">סה"כ: ${this.decimalHoursToText(dayHours)}${showExams ? ` | בדיקות: ${this.getDailyExamsCount(dateKey)}` : ''}</span>
          </div>
          <table>
            <thead><tr><th>כניסה</th><th>יציאה</th><th>שעות</th></tr></thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </div>`;
    });

    let absenceHtml = '';
    if (this.absenceRecords && this.absenceRecords.length > 0) {
      let absRows = '';
      this.absenceRecords.forEach(a => {
        const type = a.AbsenceType === 'vacation' ? 'חופשה' : 'מחלה';
        absRows += `<tr>
          <td>${this.formatDate(a.StartDate)}</td>
          <td>${this.formatDate(a.EndDate)}</td>
          <td style="text-align:center">${this.calculateAbsenceDays(a)}</td>
          <td>${type}</td>
          <td>${a.Reason || '-'}</td>
        </tr>`;
      });
      absenceHtml = `
        <h3>העדרויות</h3>
        <table>
          <thead><tr><th>מ-</th><th>עד</th><th>ימים</th><th>סוג</th><th>סיבה</th></tr></thead>
          <tbody>${absRows}</tbody>
        </table>`;
    }

    const totalVacation = this.getTotalVacationDays();
    const totalSick = this.getTotalSickDays();

    const html = `<!DOCTYPE html>
<html dir="rtl" lang="he">
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Arial, sans-serif; direction: rtl; color: #000; margin: 1.5cm 2cm; font-size: 13px; background: #fff; }
    h1 { color: #000; font-size: 20px; margin-bottom: 0.2em; border-bottom: 2px solid #000; padding-bottom: 0.3em; }
    h2 { color: #000; font-size: 13px; font-weight: normal; margin: 0 0 1.5em; }
    h3 { color: #000; font-size: 14px; margin-top: 2em; border-bottom: 1px solid #000; padding-bottom: 4px; }
    .stats { display: flex; gap: 1em; flex-wrap: wrap; margin: 1.2em 0 1.8em; }
    .stat { flex: 1; min-width: 100px; text-align: center; padding: 0.8em 0.5em; border: 1px solid #000; border-radius: 8px; }
    .stat-val { font-size: 22px; font-weight: bold; color: #000; line-height: 1.2; }
    .stat-lbl { font-size: 11px; color: #000; margin-top: 2px; }
    .day-block { margin-bottom: 1.2em; page-break-inside: avoid; border: 1px solid #000; border-radius: 6px; overflow: hidden; }
    .day-header { display: flex; justify-content: space-between; align-items: center; color: #000; padding: 7px 12px; font-size: 13px; border-bottom: 1px solid #000; }
    .day-date-name { font-weight: bold; font-size: 14px; color: #000; }
    .day-total { padding: 2px 10px; font-size: 12px; font-weight: bold; color: #000; }
    table { width: 100%; border-collapse: collapse; }
    th { color: #000; padding: 6px 10px; text-align: right; font-size: 12px; font-weight: bold; border-bottom: 1px solid #000; }
    td { border-bottom: 1px solid #000; padding: 6px 10px; font-size: 13px; }
    tr:last-child td { border-bottom: none; }
    .missing { color: #000; font-weight: bold; }
    @media print {
      body { margin: 1cm; }
    }
  </style>
</head>
<body>
  <h1>${title}</h1>
  <h2>${this.currentEmployeeNumber ? `מספר עובד: ${this.currentEmployeeNumber}` : ''}</h2>

  <div class="stats">
    <div class="stat"><div class="stat-val">${this.decimalHoursToText(totalWorkHours)}</div><div class="stat-lbl">סה"כ שעות עבודה</div></div>
    <div class="stat"><div class="stat-val">${dateKeys.length}</div><div class="stat-lbl">ימי עבודה</div></div>
    ${showExams ? `<div class="stat"><div class="stat-val">${this.monthlyStats?.totalExams ?? 0}</div><div class="stat-lbl">סה"כ בדיקות</div></div>` : ''}
    <div class="stat"><div class="stat-val">${totalVacation}</div><div class="stat-lbl">ימי חופשה</div></div>
    <div class="stat"><div class="stat-val">${totalSick}</div><div class="stat-lbl">ימי מחלה</div></div>
  </div>

  ${daysHtml}
  ${absenceHtml}
</body>
</html>`;

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 300);
  }

  /**
   * Export to Excel
   */
  exportToExcel(): void {
    // Get selected user name
    const userName = this.currentEmployeeName || this.ls.username || '';
    
    // Create CSV content
    const headers = ['תאריך', 'יום', 'שעה', 'סוג', 'שם משתמש', 'הערות'];
    let csvContent = '\uFEFF'; // Add BOM for Excel UTF-8 support
    
    // Add title with user name
    const monthName = this.getTranslatedMonthName(this.selectedMonth);
    csvContent += `דוח חודשי של ${userName} - ${monthName} ${this.selectedYear}\n`;
    
    // Add employee number if exists
    if (this.currentEmployeeNumber) {
      csvContent += `מספר עובד: ${this.currentEmployeeNumber}\n`;
    }
    
    csvContent += '\n'; // Empty line after title
    
    // Add headers
    csvContent += headers.join(',') + '\n';
    
    // Group records by date first to check for pairs
    const groupedRecords = this.getGroupedRecords();
    // Sort dates from oldest to newest using actual timestamps
    const dateKeys = Object.keys(groupedRecords).sort((a, b) => {
      const getTs = (key: string) => {
        const records = groupedRecords[key];
        return records.length ? new Date(records[0].CreationTime).getTime() : 0;
      };
      return getTs(a) - getTs(b);
    });
    
    let totalWorkHours = 0;
    let shiftsCount = 0;
    const showExams = this.shouldShowExams();

    // Add data grouped by date
    dateKeys.forEach(dateKey => {
      const dayRecords = groupedRecords[dateKey];
      const dayName = this.getHebrewDayName(dayRecords[0].CreationTime);

      // Add records for this day
      dayRecords.forEach(record => {
        const timeStr = this.formatTime(record.CreationTime);
        const typeStr = record.CheckType === 'check-in' ? 'כניסה' : 'יציאה';
        const recordUserName = record.UserName || '';

        // Check if this record has a matching pair
        const hasPair = this.hasMatchingPair(record, dayRecords);
        const note = !hasPair
          ? (record.CheckType === 'check-in' ? 'חסר זמן יציאה' : 'חסר זמן כניסה')
          : '';

        csvContent += `"${dateKey}","יום ${dayName}","${timeStr}","${typeStr}","${recordUserName}","${note}"\n`;
      });

      // Calculate daily hours
      const dayHours = this.calculateDayHoursDecimal(dayRecords);
      totalWorkHours += dayHours;

      // Count shifts for this day
      const sorted = [...dayRecords].sort((a, b) =>
        new Date(a.CreationTime).getTime() - new Date(b.CreationTime).getTime()
      );

      let lastCheckIn = null;
      for (const record of sorted) {
        if (record.CheckType === 'check-in') {
          lastCheckIn = record;
        } else if (record.CheckType === 'check-out' && lastCheckIn) {
          shiftsCount++;
          lastCheckIn = null;
        }
      }

      // Add daily summary row
      csvContent += `"סה\\"כ יומי","","","","","${this.decimalHoursToText(dayHours)}"\n`;
      if (showExams) {
        csvContent += `"סה\\"כ בדיקות יומי","","","","","${this.getDailyExamsCount(dateKey)}"\n`;
      }
      csvContent += '\n'; // Empty line between days
    });
    
    const daysWorked = dateKeys.length;
    
    // Add absence records section
    if (this.absenceRecords && this.absenceRecords.length > 0) {
      csvContent += '\n';
      csvContent += 'רשומות העדרויות\n';
      csvContent += '"תאריך התחלה","תאריך סיום","מספר ימים","סוג","סיבה"\n';
      
      this.absenceRecords.forEach(absence => {
        const startDate = this.formatDate(absence.StartDate);
        const endDate = this.formatDate(absence.EndDate);
        const days = this.calculateAbsenceDays(absence);
        const type = absence.AbsenceType === 'vacation' ? 'חופשה' : 'מחלה';
        const reason = absence.Reason || '-';
        
        csvContent += `"${startDate}","${endDate}","${days}","${type}","${reason}"\n`;
      });
      csvContent += '\n';
    }
    
    // Calculate vacation and sick days
    const totalVacationDays = this.getTotalVacationDays();
    const totalSickDays = this.getTotalSickDays();
    
    // Add monthly stats section
    csvContent += '\n';
    csvContent += 'סטטיסטיקות חודשיות\n';
    csvContent += `סה"כ שעות עבודה,${this.decimalHoursToText(totalWorkHours)}\n`;
    csvContent += `מספר משמרות,${shiftsCount}\n`;
    if (showExams) {
      csvContent += `סה"כ בדיקות,${this.monthlyStats?.totalExams ?? 0}\n`;
    }
    csvContent += `סה"כ ימי חופשה,${totalVacationDays},סה"כ ימי מחלה,${totalSickDays}\n`;
    
    // Create blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    const fileName = `attendance_report_${this.selectedYear}_${this.selectedMonth.toString().padStart(2, '0')}.csv`;
    link.setAttribute('download', fileName);
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Open modal to fix missing time
   */
  openFixMissingTime(record: AttendanceRecord, dateKey: string): void {
    this.fixingRecord = record;
    this.fixingDateKey = dateKey;
    this.newTime = '';
    this.fixError = null;
    this.showFixModal = true;
  }

  /**
   * Close fix modal
   */
  closeFixModal(): void {
    this.showFixModal = false;
    this.fixingRecord = null;
    this.fixingDateKey = '';
    this.newTime = '';
    this.fixError = null;
  }

  /**
   * Save missing time
   */
  saveMissingTime(): void {
    if (!this.fixingRecord || !this.newTime) {
      return;
    }

    this.isFixing = true;
    this.fixError = null;

    // Parse the date from the fixingDateKey and combine with the new time
    const dateParts = this.fixingDateKey.split('.');
    if (dateParts.length !== 3) {
      this.fixError = 'תאריך לא תקין';
      this.isFixing = false;
      return;
    }

    const day = dateParts[0];
    const month = dateParts[1];
    const year = dateParts[2];
    
    const timeParts = this.newTime.split(':');
    if (timeParts.length < 2) {
      this.fixError = 'שעה לא תקינה';
      this.isFixing = false;
      return;
    }

    // Create the full date-time string
    const dateTimeStr = `${year}-${month}-${day}T${this.newTime}`;
    const newDateTime = new Date(dateTimeStr);

    if (isNaN(newDateTime.getTime())) {
      this.fixError = 'תאריך או שעה לא תקינים';
      this.isFixing = false;
      return;
    }

    // Determine the check type for the new record (opposite of the existing one)
    const newCheckType = this.fixingRecord.CheckType === 'check-in' ? 'check-out' : 'check-in';

    // Call service to add the missing record
    this.attendanceService.addMissingRecord(
      this.selectedUserId || this.ls.UID,
      newDateTime.toISOString(),
      newCheckType
    ).subscribe(
      (response: any) => {
        if (response.success) {
          // Reload the report
          this.loadMonthlyReport();
          this.closeFixModal();
        } else {
          this.fixError = response.error || 'שגיאה בשמירת הרשומה';
        }
        this.isFixing = false;
      },
      (err) => {
        this.isFixing = false;
        this.fixError = 'שגיאה בשמירת הרשומה';
        console.error('Error:', err);
      }
    );
  }

  /**
   * Load absence records for the selected user and month
   */
  loadAbsenceRecords(): void {
    const userId = this.selectedUserId || this.ls.UID;
    if (!userId) return;

    // Calculate start and end dates for the month
    const startDate = `${this.selectedYear}-${String(this.selectedMonth).padStart(2, '0')}-01`;
    const lastDay = new Date(this.selectedYear, this.selectedMonth, 0).getDate();
    const endDate = `${this.selectedYear}-${String(this.selectedMonth).padStart(2, '0')}-${lastDay}`;

    this.attendanceService.getAbsenceRecords(userId, startDate, endDate).subscribe(
      (response: any) => {
        if (response.success) {
          this.absenceRecords = response.data || [];
        }
      },
      (err) => {
        console.error('Error loading absence records:', err);
      }
    );
  }

  /**
   * Open absence modal
   */
  openAbsenceModal(absence?: AbsenceRecord): void {
    this.showAbsenceModal = true;
    this.absenceError = null;
    
    if (absence) {
      // Edit mode - populate form with existing data
      this.editingAbsenceId = absence.AutoIndex;
      this.absenceForm = {
        startDate: new Date(absence.StartDate).toISOString().split('T')[0],
        endDate: new Date(absence.EndDate).toISOString().split('T')[0],
        absenceType: absence.AbsenceType,
        reason: absence.Reason || ''
      };
    } else {
      // Add mode - reset form with today as default
      this.editingAbsenceId = null;
      const today = new Date();
      this.absenceForm = {
        startDate: today.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
        absenceType: 'vacation',
        reason: ''
      };
    }
  }

  /**
   * Close absence modal
   */
  closeAbsenceModal(): void {
    this.showAbsenceModal = false;
    this.absenceError = null;
  }

  /**
   * Save absence record
   */
  saveAbsence(): void {
    this.isAbsenceSaving = true;
    this.absenceError = null;

    const userId = this.selectedUserId || this.ls.UID;
    if (!userId) {
      this.absenceError = 'משתמש לא נמצא';
      this.isAbsenceSaving = false;
      return;
    }

    // Validate dates
    if (new Date(this.absenceForm.endDate) < new Date(this.absenceForm.startDate)) {
      this.absenceError = 'תאריך סיום חייב להיות אחרי תאריך התחלה';
      this.isAbsenceSaving = false;
      return;
    }

    if (this.editingAbsenceId) {
      // Update existing absence
      this.attendanceService.updateAbsence(
        this.editingAbsenceId,
        this.absenceForm.startDate,
        this.absenceForm.endDate,
        this.absenceForm.absenceType,
        this.absenceForm.reason
      ).subscribe(
        (response: any) => {
          if (response.success) {
            this.loadAbsenceRecords();
            this.closeAbsenceModal();
          } else {
            this.absenceError = response.error || 'שגיאה בעדכון הרשומה';
          }
          this.isAbsenceSaving = false;
        },
        (err) => {
          this.isAbsenceSaving = false;
          this.absenceError = err.error?.error || 'שגיאה בעדכון הרשומה';
          console.error('Error:', err);
        }
      );
    } else {
      // Add new absence
      this.attendanceService.addAbsence(
        userId,
        this.absenceForm.startDate,
        this.absenceForm.endDate,
        this.absenceForm.absenceType,
        this.absenceForm.reason
      ).subscribe(
        (response: any) => {
          if (response.success) {
            this.loadAbsenceRecords();
            this.closeAbsenceModal();
          } else {
            this.absenceError = response.error || 'שגיאה בשמירת הרשומה';
          }
          this.isAbsenceSaving = false;
        },
        (err) => {
          this.isAbsenceSaving = false;
          this.absenceError = err.error?.error || 'שגיאה בשמירת הרשומה';
          console.error('Error:', err);
        }
      );
    }
  }

  /**
   * Calculate number of days in absence period
   */
  calculateAbsenceDays(absence: AbsenceRecord): number {
    const start = new Date(absence.StartDate);
    const end = new Date(absence.EndDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1; // Include both start and end days
  }

  /**
   * Get total vacation days for the current month
   */
  getTotalVacationDays(): number {
    if (!this.absenceRecords || this.absenceRecords.length === 0) {
      return 0;
    }
    
    return this.absenceRecords
      .filter(absence => absence.AbsenceType === 'vacation')
      .reduce((total, absence) => total + this.calculateAbsenceDays(absence), 0);
  }

  /**
   * Get total sick days for the current month
   */
  getTotalSickDays(): number {
    if (!this.absenceRecords || this.absenceRecords.length === 0) {
      return 0;
    }
    
    return this.absenceRecords
      .filter(absence => absence.AbsenceType === 'sick')
      .reduce((total, absence) => total + this.calculateAbsenceDays(absence), 0);
  }

  /**
   * Open delete confirmation modal
   */
  openDeleteConfirmation(id: number): void {
    this.deletingAbsenceId = id;
    this.showDeleteConfirmModal = true;
  }

  /**
   * Close delete confirmation modal
   */
  closeDeleteConfirmation(): void {
    this.showDeleteConfirmModal = false;
    this.deletingAbsenceId = null;
  }

  /**
   * Confirm and delete absence record
   */
  confirmDeleteAbsence(): void {
    if (!this.deletingAbsenceId) return;

    this.attendanceService.deleteAbsence(this.deletingAbsenceId).subscribe(
      (response: any) => {
        if (response.success) {
          this.loadAbsenceRecords();
          this.closeDeleteConfirmation();
        } else {
          alert('שגיאה במחיקת הרשומה');
        }
      },
      (err) => {
        console.error('Error deleting absence record:', err);
        alert('שגיאה במחיקת הרשומה');
        this.closeDeleteConfirmation();
      }
    );
  }
}
